import { useEffect, useState } from 'react';
import { coreService } from '../../api';
import { useAuth } from '../../auth';
import { useOps } from '../../hooks/useOps';
import { cn } from '../../lib/utils';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  setFetchingConfig,
  updateOpsConfig,
} from '../../store/slices/opsSlice';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { initializeUserOps, currentOps } = useOps();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const { opsConfigs, fetchingConfigs } = useAppSelector((state) => state.ops);

  useEffect(() => {
    if (user) {
      initializeUserOps();
    }
  }, [user, initializeUserOps]);

  useEffect(() => {
    const fetchOpsConfiguration = async () => {
      if (!currentOps) {
        return;
      }

      if (fetchingConfigs[currentOps]) {
        return;
      }

      const existingConfig = opsConfigs[currentOps];
      if (existingConfig && existingConfig.dbName && existingConfig.screens) {
        return;
      }

      try {
        dispatch(setFetchingConfig({ ops: currentOps, fetching: true }));

        const response = await coreService.getOpsConfig(currentOps);
        const { screens, dbName, uploads } = response.data;

        dispatch(
          updateOpsConfig({
            ops: currentOps,
            config: {
              dbName: dbName,
              screens: screens || [],
              uploads: uploads || [],
            },
          })
        );
      } catch (error) {
        console.error('Failed to fetch ops configuration:', error);
      } finally {
        dispatch(setFetchingConfig({ ops: currentOps, fetching: false }));
      }
    };

    fetchOpsConfiguration();
  }, [currentOps, dispatch, opsConfigs, fetchingConfigs]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="h-screen flex overflow-hidden bg-[hsl(210,40%,98%)]">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex flex-col flex-1 min-w-0">
        <Header onMenuClick={handleSidebarToggle} sidebarOpen={sidebarOpen} />

        <main
          className={cn(
            'flex-1 overflow-auto pt-16 transition-all duration-200',
            sidebarOpen ? 'md:ml-64' : 'ml-0'
          )}
        >
          <div className="container mx-auto p-6 max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  );
}
